<script setup lang="ts">
import FaceRatingHappyIcon from '@/assets/icons/FaceRatingHappyIcon.vue';
import FaceRatingNeutralIcon from '@/assets/icons/FaceRatingNeutralIcon.vue';
import FaceRatingSadIcon from '@/assets/icons/FaceRatingSadIcon.vue';
import { useMobile } from '@/composables/useMobile';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import { SUPPORT_MEASURE_PROTOCOL_TEXT_MAX_LENGTH } from 'common/constants/support.measure.constants';
import { computed, ref, useTemplateRef } from 'vue';

export interface ICompleteSupportMeasureResult {
  note: string | null;
  rating: number | null;
}

const emit = defineEmits<{ complete: [result: ICompleteSupportMeasureResult] }>();

const { isMobile } = useMobile();

const dialogRef = useTemplateRef('dialogRef');

const studentCount = ref(0);
const note = ref('');
const rating = ref<number | null>(null);

const RATING_OPTIONS = [
  { value: 1, icon: FaceRatingSadIcon, label: 'supportMeasureEntry.ratingNegative' },
  { value: 2, icon: FaceRatingNeutralIcon, label: 'supportMeasureEntry.ratingNeutral' },
  { value: 3, icon: FaceRatingHappyIcon, label: 'supportMeasureEntry.ratingPositive' },
] as const;

const dialogClass = computed<string>(() =>
  isMobile.value
    ? 'fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-visible rounded-none border-0 bg-transparent p-0 outline-none'
    : 'rounded-dialog max-h-[680px] overflow-hidden',
);

const show = (params: { studentCount: number }): void => {
  studentCount.value = params.studentCount;
  note.value = '';
  rating.value = null;
  dialogRef.value?.showModal();
};

const cancel = (): void => dialogRef.value?.close();

const selectRating = (value: number): void => {
  rating.value = rating.value === value ? null : value;
};

const confirm = (): void => {
  emit('complete', { note: note.value.trim() || null, rating: rating.value });
  dialogRef.value?.close();
};

defineExpose({ show });
</script>

<template>
  <NativeDialog ref="dialogRef" :class="dialogClass" :close-on-backdrop-click="false" :close-on-cancel="false">
    <section
      :class="
        isMobile
          ? 'fixed inset-x-0 bottom-0 flex max-h-mobile-drawer-tall flex-col overflow-y-auto rounded-t-dialog bg-white px-lg pb-mobile-safe-bottom-lg pt-md shadow-popover'
          : 'flex max-h-[680px] w-full max-w-[640px] flex-col overflow-y-auto px-content py-xl shadow-popover'
      "
    >
      <header class="flex shrink-0 items-center gap-md">
        <h1 class="text-title font-medium tracking-[0.23px]">{{ $t('supportMeasureEntry.completeDialogTitle') }}</h1>
      </header>

      <p class="pt-lg leading-[170%]">
        {{ $t('supportMeasureEntry.completeDialogDescription', { count: studentCount }) }}
      </p>

      <label class="pt-xl font-semibold" for="completion-note">
        {{ $t('supportMeasureEntry.completionNoteLabel') }}
      </label>
      <div
        class="mt-sm w-full rounded-input border border-divider bg-white p-md transition-colors focus-within:border-2 focus-within:border-tint"
      >
        <textarea
          id="completion-note"
          v-model="note"
          class="text-body min-h-[200px] w-full resize-none bg-transparent outline-none"
          rows="6"
          :maxlength="SUPPORT_MEASURE_PROTOCOL_TEXT_MAX_LENGTH"
          :placeholder="$t('supportMeasureEntry.completionNotePlaceholder')"
        />
      </div>

      <div class="mt-md flex items-center gap-md">
        <span class="text-secondary">{{ $t('supportMeasureEntry.completionRatingLabel') }}</span>
        <div
          class="flex items-center gap-xs"
          role="radiogroup"
          :aria-label="$t('supportMeasureEntry.completionRatingLabel')"
        >
          <button
            v-for="option in RATING_OPTIONS"
            :key="option.value"
            type="button"
            role="radio"
            :aria-checked="rating === option.value"
            :aria-label="$t(option.label)"
            class="flex size-9 items-center justify-center rounded-full transition-colors"
            :class="rating === option.value ? 'bg-highlight text-tint' : 'text-secondary hover:text-tint'"
            @click="selectRating(option.value)"
          >
            <component :is="option.icon" class="size-icon-md" />
          </button>
        </div>
      </div>

      <footer :class="['flex gap-sm pt-2xl', isMobile ? 'flex-col items-stretch' : 'items-center justify-end']">
        <CustomButton
          :class="[isMobile ? 'w-full' : 'w-full sm:w-auto', 'justify-center']"
          theme="secondary"
          @click="cancel"
        >
          {{ $t('general.cancel') }}
        </CustomButton>
        <CustomButton
          :class="[isMobile ? 'w-full' : 'w-full sm:w-auto', 'justify-center']"
          theme="primary"
          @click="confirm"
        >
          {{ $t('supportMeasureEntry.complete') }}
        </CustomButton>
      </footer>
    </section>
  </NativeDialog>
</template>
